import { useQuery } from '@tanstack/react-query';
import { getScheduledDeploymentDetails } from 'Services';
import { RQ_SCHEDULED_DEPLOYMENT_DETAILS, getCustomUserName } from 'Utils';
import { paramHelper } from '../helper';

const useGetScheduledDeploymentDetails = (users?: UsersHashmap) => {
    return useQuery([RQ_SCHEDULED_DEPLOYMENT_DETAILS], getScheduledDeploymentDetails, {
        select: (data: ScheduledDeploymentDetails) => {
            // map acquirerRevision.rules by ruleId and use it to populate missing desc on tenantGroupRevisions.rules
            const acquirerRevisionRulesMap: { [key: string]: Rule } = {};

            const acquirerRevision = {
                ...data.details.acquirerRevision,
                config: {
                    ...data.details.acquirerRevision.config,
                    _formatted: {
                        ...data.details.acquirerRevision.config?._formatted,
                        ...paramHelper(data.details.acquirerRevision.config ?? {}, ['description'], 'parameters')
                    }
                },
                rules: data.details.acquirerRevision.rules.map((rule) => {
                    acquirerRevisionRulesMap[rule.id] = { ...rule };
                    return {
                        ...rule,
                        virtualRuleGroup: rule.ruleGroup,
                        hiddenFromTenantString: rule.hiddenFromTenant ? 'Yes' : 'No',
                        allowOverridesString: rule.allowOverrides ? 'Yes' : 'No',
                        _formatted: {
                            active: rule.parameters.active ? 'Active' : 'Inactive',
                            inputDescription: rule.inputDescription,
                            outputDescription: rule.outputDescription
                        }
                    };
                })
            };

            const merchantRevisions = data.details.merchantRevisions.map((merchantRevision) => ({
                ...merchantRevision,
                merchantEnabled: merchantRevision.config?.merchantEnabled,
                merchantName: merchantRevision.config?.merchantName,
                merchantDescription: merchantRevision.config?.merchantDescription,
                activeRules: merchantRevision.rules.filter((rule) => rule.parameters.active).length,
                rules: [...merchantRevision.rules, ...acquirerRevision.rules],
                _formatted: {
                    merchantEnabled: merchantRevision.config?.merchantEnabled ? 'Active' : 'Inactive'
                }
            }));

            const tenantGroupRevisions = data.details.tenantGroupRevisions.map((tenantGroupRevision) => {
                return {
                    ...tenantGroupRevision,
                    rules: tenantGroupRevision?.rules.map((rule) => {
                        let outputDescription = '';
                        if (rule.actions?.length) {
                            const operatorValue = rule.actions[0].operator;
                            switch (operatorValue) {
                                case 'ACCEPT': {
                                    outputDescription = 'Accept';
                                    break;
                                }
                                case 'REJECT': {
                                    outputDescription = 'Reject';
                                    break;
                                }
                                case 'REVIEW': {
                                    outputDescription = 'Review';
                                    break;
                                }
                                case 'UPDATE_SCORE_BY': {
                                    let updateValue = '';
                                    if (Array.isArray(rule.actions[0].values)) {
                                        updateValue = rule.actions[0].values[0];
                                    }
                                    outputDescription = `Update Score by ${updateValue}`;
                                    break;
                                }
                                default: {
                                    outputDescription = '--';
                                }
                            }
                        }
                        return {
                            ...rule,
                            // TODO: hard code to Override, but should be evaluated using -> resolveRuleType(rule)
                            ruleType: 'Override',
                            _formatted: {
                                active: rule.parameters.active ? 'Active' : 'Inactive',
                                inputDescription:
                                    rule.inputDescription || acquirerRevisionRulesMap[rule.id].inputDescription,
                                outputDescription
                            }
                        };
                    })
                };
            });

            const deploymentData = {
                status: data.status,
                id: data.id,
                tenant: data.tenant,
                templateRepositoryId: data.templateRepositoryId,
                comment: data.comment,
                tenantGroupData: data?.details.deploymentParams.tenantGroupData,
                tenantGroupOverviewData: data?.details.deploymentParams.tenantGroupOverviewData,
                summary: data?.details.summary,
                createdBy: getCustomUserName(data.userId, users),
                scheduledBy: getCustomUserName(data.userId, users),
                scheduledFor: data.scheduledTime
            } as Deployment;

            return {
                deploymentData,
                acquirerRevision,
                merchantRevisions,
                tenantGroupRevisions
            };
        }
    });
};

export default useGetScheduledDeploymentDetails;
